import API from '../api/api'

/**
 * Whether the shop holds the AI translation closed-beta feature.
 *
 * The feature is granted per shop by staff and arrives with the shop payload as
 * `langify_features`. A missing list or missing entry means not granted.
 *
 * @param {Array|null|undefined} langifyFeatures - shop.shop.langify_features
 * @returns {boolean}
 */
export const hasAiTranslationFeature = (langifyFeatures) =>
  langifyFeatures?.find(f => f && f.name === 'ai_translation')?.is_enabled === true;

/**
 * Whether the shop may use the bulk translation: the Growth plan and above include it, on the
 * Basic plan staff grants the `auto_translation_bulk` feature per shop. The feature opens the
 * whole tool, its settings included; the free plan gets none of it, feature or not.
 *
 * @param {string|undefined} chargeName - shop.shop.charge?.name
 * @param {Array|null|undefined} langifyFeatures - shop.shop.langify_features
 * @returns {boolean}
 */
export const hasBulkTranslationAccess = (chargeName, langifyFeatures) =>
  (!!chargeName && chargeName !== 'free' && chargeName !== 'basic')
  || (chargeName === 'basic' && langifyFeatures?.find(f => f && f.name === 'auto_translation_bulk')?.is_enabled === true);

/**
 * Resolves the effective bulk translation type for the current shop.
 *
 * The stored setting can be "ai", but AI bulk translation requires both the closed-beta
 * feature and bulk translation access (Growth plan or the bulk feature). Whenever one of
 * them is missing we fall back to "default" (NMT via DeepL / Google), regardless of what is
 * stored — so a shop taken out of the beta silently continues on the regular bulk translation.
 *
 * Omitting `langifyFeatures` resolves to "default": the gate fails closed on purpose, a
 * caller that does not pass the features must never end up showing AI as active.
 *
 * The backend enforces the same rules in ConfigRepository::getBulkTranslationType() — this
 * is for the UI only and is not the authority.
 *
 * @param {Array|Object|null} bulkSettingsData - state.config.bulk_settings_data
 * @param {string|undefined} chargeName - shop.shop.charge?.name
 * @param {Array|null|undefined} langifyFeatures - shop.shop.langify_features
 * @returns {'default'|'ai'}
 */
export const resolveBulkTranslationType = (bulkSettingsData, chargeName, langifyFeatures) => {
  const stored = Array.isArray(bulkSettingsData)
    ? bulkSettingsData.find(i => i && i.slug === 'translation_type')?.value
    : bulkSettingsData?.value;
  return (stored === 'ai' && hasBulkTranslationAccess(chargeName, langifyFeatures) && hasAiTranslationFeature(langifyFeatures)) ? 'ai' : 'default';
}

/**
 * Normalizes settings data to always be an array.
 * - Array input: returned as-is
 * - Object input: values are extracted (keys are assumed to be numeric indices)
 * - Anything else: returned as-is (null, undefined)
 */
const normalizeToArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data !== null && typeof data === 'object') return Object.values(data);
  return data;
}

// ACTION TYPES
const GET_SETTINGS_PENDING = 'GET_SETTINGS_PENDING'
const GET_SETTINGS_RESPONSE = 'GET_SETTINGS_RESPONSE'
const GET_SETTINGS_DEFAULT_PENDING = 'GET_SETTINGS_DEFAULT_PENDING'
const GET_SETTINGS_DEFAULT_RESPONSE = 'GET_SETTINGS_DEFAULT_RESPONSE'
const SAVE_SETTINGS_PENDING = 'SAVE_SETTINGS_PENDING'
const SAVE_SETTINGS_RESPONSE = 'SAVE_SETTINGS_RESPONSE'
const DELETE_SETTINGS_PENDING = 'DELETE_SETTINGS_PENDING'
const DELETE_SETTINGS_RESPONSE = 'DELETE_SETTINGS_RESPONSE'
const RESET_SETTINGS_ERRORS = 'RESET_SETTINGS_ERRORS'
const GET_DEEPL_SETTINGS_PENDING = 'GET_DEEPL_SETTINGS_PENDING'
const GET_DEEPL_SETTINGS_RESPONSE = 'GET_DEEPL_SETTINGS_RESPONSE'
const GET_DEEPL_SETTINGS_DEFAULT_PENDING = 'GET_DEEPL_SETTINGS_DEFAULT_PENDING'
const GET_DEEPL_SETTINGS_DEFAULT_RESPONSE = 'GET_DEEPL_SETTINGS_DEFAULT_RESPONSE'
const SAVE_DEEPL_SETTINGS_PENDING = 'SAVE_DEEPL_SETTINGS_PENDING'
const SAVE_DEEPL_SETTINGS_RESPONSE = 'SAVE_DEEPL_SETTINGS_RESPONSE'
const RESET_DEEPL_SETTINGS = 'RESET_DEEPL_SETTINGS'
const GET_TRANSLATION_INSTRUCTIONS_RESPONSE = 'GET_TRANSLATION_INSTRUCTIONS_RESPONSE'
const GET_GLOSSARY_SETTINGS_PENDING = 'GET_GLOSSARY_SETTINGS_PENDING'
const GET_GLOSSARY_SETTINGS_RESPONSE = 'GET_GLOSSARY_SETTINGS_RESPONSE'
const GET_GLOSSARY_SETTINGS_DEFAULT_PENDING = 'GET_GLOSSARY_SETTINGS_DEFAULT_PENDING'
const GET_GLOSSARY_SETTINGS_DEFAULT_RESPONSE = 'GET_GLOSSARY_SETTINGS_DEFAULT_RESPONSE'
const SAVE_GLOSSARY_SETTINGS_PENDING = 'SAVE_GLOSSARY_SETTINGS_PENDING'
const SAVE_GLOSSARY_SETTINGS_RESPONSE = 'SAVE_GLOSSARY_SETTINGS_RESPONSE'
const GET_BULK_SETTINGS_PENDING = 'GET_BULK_SETTINGS_PENDING'
const GET_BULK_SETTINGS_RESPONSE = 'GET_BULK_SETTINGS_RESPONSE'
const GET_BULK_SETTINGS_DEFAULT_PENDING = 'GET_BULK_SETTINGS_DEFAULT_PENDING'
const GET_BULK_SETTINGS_DEFAULT_RESPONSE = 'GET_BULK_SETTINGS_DEFAULT_RESPONSE'
const SAVE_BULK_SETTINGS_PENDING = 'SAVE_BULK_SETTINGS_PENDING'
const SAVE_BULK_SETTINGS_RESPONSE = 'SAVE_BULK_SETTINGS_RESPONSE'

const initialState = {
  get_settings_pending: false,
  get_settings_default_pending: false,
  save_settings_pending: false,
  delete_settings_pending: false,
  error: false,
  settings_data: null,
  default_settings_data: null,
  last_saved: null,
  get_deepl_settings_pending: false,
  get_deepl_settings_default_pending: false,
  save_deepl_settings_pending: false,
  deepl_settings_data: null,
  deepl_settings_default_data: null,
  // staff-set context and custom instructions, read-only here: {context, custom_instructions: {locale: text}}
  translation_instructions: null,
  deepl_last_saved: null,
  get_glossary_settings_pending: false,
  get_glossary_settings_default_pending: false,
  save_glossary_settings_pending: false,
  glossary_settings_data: null,
  glossary_settings_default_data: null,
  glossary_last_saved: null,
  get_bulk_settings_pending: false,
  get_bulk_settings_default_pending: false,
  save_bulk_settings_pending: false,
  bulk_settings_data: null,
  bulk_settings_default_data: null,
  bulk_last_saved: null,
}

// REDUCER
export default (state = initialState, action) => {
  switch(action.type) {
    case RESET_SETTINGS_ERRORS: {
      return {
        ...state,
        error: false,
      }
    }

    case GET_SETTINGS_PENDING: {
      return {
        ...state,
        get_settings_pending: true,
        error: false,
      }
    }
    case GET_SETTINGS_RESPONSE: {
      if (action.response.success === true) {
        return {
          ...state,
          get_settings_pending: false,
          error: false,
          settings_data: normalizeToArray(action.response.data || action.response.config || action.response),
        }
      } else if (action.response.success === false) {
        return {
          ...state,
          get_settings_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to load configuration',
            action: action.action,
            level: 0,
          },
        }
      } else {
        // Response with no success field - treat as data
        return {
          ...state,
          get_settings_pending: false,
          error: false,
          settings_data: normalizeToArray(action.response),
        }
      }
    }

    case GET_SETTINGS_DEFAULT_PENDING: {
      return {
        ...state,
        get_settings_default_pending: true,
        error: false,
      }
    }
    case GET_SETTINGS_DEFAULT_RESPONSE: {
      if (action.response.success === true) {
        return {
          ...state,
          get_settings_default_pending: false,
          error: false,
          default_settings_data: normalizeToArray(action.response.data || action.response.config || action.response),
        }
      } else if (action.response.success === false) {
        return {
          ...state,
          get_settings_default_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to load default configuration',
            action: action.action,
            level: 0,
          },
        }
      } else {
        // Response with no success field - treat as data
        return {
          ...state,
          get_settings_default_pending: false,
          error: false,
          default_settings_data: normalizeToArray(action.response),
        }
      }
    }

    case SAVE_SETTINGS_PENDING: {
      return {
        ...state,
        save_settings_pending: true,
        error: false,
      }
    }
    case SAVE_SETTINGS_RESPONSE: {
      if (action.response.success === true || action.response.success === undefined) {
        return {
          ...state,
          save_settings_pending: false,
          error: false,
          last_saved: Date.now(),
          settings_data: normalizeToArray(action.response.data || action.response.config) || state.settings_data,
        }
      } else {
        return {
          ...state,
          save_settings_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to save configuration',
            action: action.action,
            level: 1,
          },
        }
      }
    }

    case DELETE_SETTINGS_PENDING: {
      return {
        ...state,
        delete_settings_pending: true,
        error: false,
      }
    }
    case DELETE_SETTINGS_RESPONSE: {
      if (action.response.success === true || action.response.success === undefined) {
        return {
          ...state,
          delete_settings_pending: false,
          error: false,
          settings_data: null,
        }
      } else {
        return {
          ...state,
          delete_settings_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to delete configuration',
            action: action.action,
            level: 1,
          },
        }
      }
    }

    case GET_DEEPL_SETTINGS_PENDING: {
      return {
        ...state,
        get_deepl_settings_pending: true,
        error: false,
      }
    }
    case GET_DEEPL_SETTINGS_RESPONSE: {
      if (action.response.success === false) {
        return {
          ...state,
          get_deepl_settings_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to load DeepL settings',
            action: action.action,
            level: 0,
          },
        }
      }
      const rawDeeplData = action.response.data || action.response;
      return {
        ...state,
        get_deepl_settings_pending: false,
        error: false,
        deepl_settings_data: Array.isArray(rawDeeplData) ? rawDeeplData : [rawDeeplData],
      }
    }

    case GET_TRANSLATION_INSTRUCTIONS_RESPONSE: {
      // no error banner: the block simply stays hidden when this fails
      return {
        ...state,
        translation_instructions: action.response?.success ? action.response.data : null,
      }
    }

    case GET_DEEPL_SETTINGS_DEFAULT_PENDING: {
      return {
        ...state,
        get_deepl_settings_default_pending: true,
        error: false,
      }
    }
    case GET_DEEPL_SETTINGS_DEFAULT_RESPONSE: {
      if (action.response.success === false) {
        return {
          ...state,
          get_deepl_settings_default_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to load DeepL default settings',
            action: action.action,
            level: 0,
          },
        }
      }
      return {
        ...state,
        get_deepl_settings_default_pending: false,
        error: false,
        deepl_settings_default_data: Array.isArray(action.response.data) ? action.response.data : action.response.data ? [action.response.data] : null,
      }
    }

    case SAVE_DEEPL_SETTINGS_PENDING: {
      return {
        ...state,
        save_deepl_settings_pending: true,
        error: false,
      }
    }
    case RESET_DEEPL_SETTINGS: {
      return {
        ...state,
        deepl_settings_data: null,
      }
    }

    case SAVE_DEEPL_SETTINGS_RESPONSE: {
      if (action.response.success === false) {
        return {
          ...state,
          save_deepl_settings_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to save DeepL settings',
            action: action.action,
            level: 1,
          },
        }
      }
      return {
        ...state,
        save_deepl_settings_pending: false,
        error: false,
        deepl_last_saved: Date.now(),
        deepl_settings_data: (() => { const d = action.response.data || action.response.config; return d ? (Array.isArray(d) ? d : [d]) : state.deepl_settings_data; })(),
      }
    }

    case GET_GLOSSARY_SETTINGS_PENDING: {
      return {
        ...state,
        get_glossary_settings_pending: true,
        error: false,
      }
    }
    case GET_GLOSSARY_SETTINGS_RESPONSE: {
      if (action.response.success === false) {
        return {
          ...state,
          get_glossary_settings_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to load Glossary settings',
            action: action.action,
            level: 0,
          },
        }
      }
      const rawGlossaryData = action.response.data || action.response;
      return {
        ...state,
        get_glossary_settings_pending: false,
        error: false,
        glossary_settings_data: Array.isArray(rawGlossaryData) ? rawGlossaryData : [rawGlossaryData],
      }
    }

    case GET_GLOSSARY_SETTINGS_DEFAULT_PENDING: {
      return {
        ...state,
        get_glossary_settings_default_pending: true,
        error: false,
      }
    }
    case GET_GLOSSARY_SETTINGS_DEFAULT_RESPONSE: {
      if (action.response.success === false) {
        return {
          ...state,
          get_glossary_settings_default_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to load Glossary default settings',
            action: action.action,
            level: 0,
          },
        }
      }
      return {
        ...state,
        get_glossary_settings_default_pending: false,
        error: false,
        glossary_settings_default_data: Array.isArray(action.response.data) ? action.response.data : action.response.data ? [action.response.data] : null,
      }
    }

    case SAVE_GLOSSARY_SETTINGS_PENDING: {
      return {
        ...state,
        save_glossary_settings_pending: true,
        error: false,
      }
    }
    case SAVE_GLOSSARY_SETTINGS_RESPONSE: {
      if (action.response.success === false) {
        return {
          ...state,
          save_glossary_settings_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to save Glossary settings',
            action: action.action,
            level: 1,
          },
        }
      }
      return {
        ...state,
        save_glossary_settings_pending: false,
        error: false,
        glossary_last_saved: Date.now(),
        glossary_settings_data: (() => { const d = action.response.data || action.response.config; return d ? (Array.isArray(d) ? d : [d]) : state.glossary_settings_data; })(),
      }
    }

    case GET_BULK_SETTINGS_PENDING: {
      return {
        ...state,
        get_bulk_settings_pending: true,
        error: false,
      }
    }
    case GET_BULK_SETTINGS_RESPONSE: {
      if (action.response.success === false) {
        return {
          ...state,
          get_bulk_settings_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to load Bulk settings',
            action: action.action,
            level: 0,
          },
        }
      }
      const rawBulkData = action.response.data || action.response;
      return {
        ...state,
        get_bulk_settings_pending: false,
        error: false,
        bulk_settings_data: Array.isArray(rawBulkData) ? rawBulkData : [rawBulkData],
      }
    }

    case GET_BULK_SETTINGS_DEFAULT_PENDING: {
      return {
        ...state,
        get_bulk_settings_default_pending: true,
        error: false,
      }
    }
    case GET_BULK_SETTINGS_DEFAULT_RESPONSE: {
      if (action.response.success === false) {
        return {
          ...state,
          get_bulk_settings_default_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to load Bulk default settings',
            action: action.action,
            level: 0,
          },
        }
      }
      return {
        ...state,
        get_bulk_settings_default_pending: false,
        error: false,
        bulk_settings_default_data: Array.isArray(action.response.data) ? action.response.data : action.response.data ? [action.response.data] : null,
      }
    }

    case SAVE_BULK_SETTINGS_PENDING: {
      return {
        ...state,
        save_bulk_settings_pending: true,
        error: false,
      }
    }
    case SAVE_BULK_SETTINGS_RESPONSE: {
      if (action.response.success === false) {
        return {
          ...state,
          save_bulk_settings_pending: false,
          error: {
            message: action.response?.message || action.response?.error || 'Failed to save Bulk settings',
            action: action.action,
            level: 1,
          },
        }
      }
      return {
        ...state,
        save_bulk_settings_pending: false,
        error: false,
        bulk_last_saved: Date.now(),
        bulk_settings_data: (() => { const d = action.response.data || action.response.config; return d ? (Array.isArray(d) ? d : [d]) : state.bulk_settings_data; })(),
      }
    }

    default:
      return state
  }
}

// ACTION CREATORS

/**
 * Reset settings errors
 */
export const resetSettingsErrors = () => {
  return dispatch => {
    dispatch({
      type: RESET_SETTINGS_ERRORS
    });
  }
}

/**
 * Get configuration data
 * @param {...string} pathSegments - Optional path segments for the config endpoint
 * @param {function} callback - Callback function to execute after response
 *
 * Usage:
 *   getSettings(callback) - Get /config
 *   getSettings('key_filter', callback) - Get /config/key_filter
 *   getSettings('key', 'filter', callback) - Get /config/key/filter
 */
export const getSettings = (...args) => {
  return dispatch => {
    dispatch({
      type: GET_SETTINGS_PENDING
    })

    API.getConfig(...args, response => {
      dispatch({
        type: GET_SETTINGS_RESPONSE,
        response: response,
        action: () => getSettings(...args)
      });

      // Call the original callback if provided
      const callback = args[args.length - 1];
      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Get default configuration data
 * Fetches /v2/api/config/key/filter/default
 * @param {function} callback - Callback function to execute after response
 *
 * Usage:
 *   getSettingsDefault(callback)
 */
export const getSettingsDefault = (callback) => {
  return dispatch => {
    dispatch({
      type: GET_SETTINGS_DEFAULT_PENDING
    })

    API.getConfigDefault(response => {
      dispatch({
        type: GET_SETTINGS_DEFAULT_RESPONSE,
        response: response,
        action: () => getSettingsDefault(callback)
      });

      // Call the original callback if provided
      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Save configuration data
 * POST /v2/api/settings/save
 * @param {object|array} data - Configuration data to save
 * @param {function} callback - Optional callback function to execute after response
 */
export const saveSettings = (data, callback) => {
  return dispatch => {
    dispatch({
      type: SAVE_SETTINGS_PENDING
    })

    API.saveConfig(data, response => {
      dispatch({
        type: SAVE_SETTINGS_RESPONSE,
        response: response,
        action: () => saveSettings(data, callback)
      });

      // Call the original callback if provided
      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Delete configuration data
 * @param {...string} pathSegments - Optional path segments for the config endpoint
 * @param {function} callback - Callback function to execute after response
 *
 * Usage:
 *   deleteSettings(callback) - DELETE /config
 *   deleteSettings('key_filter', callback) - DELETE /config/key_filter
 *   deleteSettings('key', 'filter', callback) - DELETE /config/key/filter
 */
export const deleteSettings = (...args) => {
  return dispatch => {
    dispatch({
      type: DELETE_SETTINGS_PENDING
    })

    API.deleteConfig(...args, response => {
      dispatch({
        type: DELETE_SETTINGS_RESPONSE,
        response: response,
        action: () => deleteSettings(...args)
      });

      // Call the original callback if provided
      const callback = args[args.length - 1];
      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Get DeepL settings
 * GET /v2/api/settings/deepl
 * @param {function} callback - Optional callback
 */
export const getDeeplSettings = (callback) => {
  return dispatch => {
    dispatch({ type: GET_DEEPL_SETTINGS_PENDING })

    API.getDeeplSettings(response => {
      dispatch({
        type: GET_DEEPL_SETTINGS_RESPONSE,
        response: response,
        action: () => getDeeplSettings(callback)
      });

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Get the staff-set translation context and custom instructions
 * GET /v2/api/settings/translation_instructions
 */
export const getTranslationInstructions = () => {
  return dispatch => {
    API.getTranslationInstructions(response => {
      dispatch({ type: GET_TRANSLATION_INSTRUCTIONS_RESPONSE, response: response });
    });
  }
}

/**
 * Get DeepL default settings
 * GET /v2/api/settings/deepl/default
 * @param {function} callback - Optional callback
 */
export const getDeeplSettingsDefault = (callback) => {
  return dispatch => {
    dispatch({ type: GET_DEEPL_SETTINGS_DEFAULT_PENDING })

    API.getDeeplSettingsDefault(response => {
      dispatch({
        type: GET_DEEPL_SETTINGS_DEFAULT_RESPONSE,
        response: response,
        action: () => getDeeplSettingsDefault(callback)
      });

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Reset DeepL settings to default (clears saved settings so defaults take effect)
 */
export const resetDeeplSettings = () => {
  return dispatch => {
    dispatch({ type: RESET_DEEPL_SETTINGS });
  }
}

/**
 * Save DeepL settings
 * POST /v2/api/settings/deepl/save
 * @param {array} data - Array of setting objects to save
 * @param {function} callback - Optional callback
 */
export const saveDeeplSettings = (data, callback) => {
  return dispatch => {
    dispatch({ type: SAVE_DEEPL_SETTINGS_PENDING })

    API.saveDeeplSettings(data, response => {
      dispatch({
        type: SAVE_DEEPL_SETTINGS_RESPONSE,
        response: response,
        action: () => saveDeeplSettings(data, callback)
      });

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Get Glossary settings
 * GET /v2/api/settings/glossary
 * @param {function} callback - Optional callback
 */
export const getGlossarySettings = (callback) => {
  return dispatch => {
    dispatch({ type: GET_GLOSSARY_SETTINGS_PENDING })

    API.getGlossarySettings(response => {
      dispatch({
        type: GET_GLOSSARY_SETTINGS_RESPONSE,
        response: response,
        action: () => getGlossarySettings(callback)
      });

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Get Glossary default settings
 * GET /v2/api/settings/glossary/default
 * @param {function} callback - Optional callback
 */
export const getGlossarySettingsDefault = (callback) => {
  return dispatch => {
    dispatch({ type: GET_GLOSSARY_SETTINGS_DEFAULT_PENDING })

    API.getGlossarySettingsDefault(response => {
      dispatch({
        type: GET_GLOSSARY_SETTINGS_DEFAULT_RESPONSE,
        response: response,
        action: () => getGlossarySettingsDefault(callback)
      });

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Save Glossary settings
 * POST /v2/api/settings/glossary/save
 * @param {array} data - Array of setting objects to save
 * @param {function} callback - Optional callback
 */
export const saveGlossarySettings = (data, callback) => {
  return dispatch => {
    dispatch({ type: SAVE_GLOSSARY_SETTINGS_PENDING })

    API.saveGlossarySettings(data, response => {
      dispatch({
        type: SAVE_GLOSSARY_SETTINGS_RESPONSE,
        response: response,
        action: () => saveGlossarySettings(data, callback)
      });

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Get Bulk settings
 * GET /v2/api/settings/bulk
 * @param {function} callback - Optional callback
 */
export const getBulkSettings = (callback) => {
  return dispatch => {
    dispatch({ type: GET_BULK_SETTINGS_PENDING })

    API.getBulkSettings(response => {
      dispatch({
        type: GET_BULK_SETTINGS_RESPONSE,
        response: response,
        action: () => getBulkSettings(callback)
      });

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Get Bulk default settings
 * GET /v2/api/settings/bulk/default
 * @param {function} callback - Optional callback
 */
export const getBulkSettingsDefault = (callback) => {
  return dispatch => {
    dispatch({ type: GET_BULK_SETTINGS_DEFAULT_PENDING })

    API.getBulkSettingsDefault(response => {
      dispatch({
        type: GET_BULK_SETTINGS_DEFAULT_RESPONSE,
        response: response,
        action: () => getBulkSettingsDefault(callback)
      });

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}

/**
 * Save Bulk settings
 * POST /v2/api/settings/bulk/save
 * @param {array} data - Array of setting objects to save
 * @param {function} callback - Optional callback
 */
export const saveBulkSettings = (data, callback) => {
  return dispatch => {
    dispatch({ type: SAVE_BULK_SETTINGS_PENDING })

    API.saveBulkSettings(data, response => {
      dispatch({
        type: SAVE_BULK_SETTINGS_RESPONSE,
        response: response,
        action: () => saveBulkSettings(data, callback)
      });

      if (typeof callback === 'function') {
        callback(response);
      }
    });
  }
}
